import type {BrowserObservationTime} from './browser-observation-clock'
import type {UiReadinessRole, UiReadinessSurface, UiReadinessVisibility} from './ui-readiness-contract'

export const CLIENT_INTERACTION_EVENT = 'one_copilot.web.client_interaction'
export const CLIENT_INTERACTION_POLICY = {
  policy_version: 'client_interaction_v2',
  gate_policy: 'one_copilot_chat',
  sampling_policy: 'all_flagged_eligible_v1',
  eligibility_policy: 'writable_committed_v1',
} as const

export type ClientInteractionKind = 'send' | 'queue' | 'steer' | 'stop'
export type ClientInteractionFeedbackMethod = 'committed_control_visible_frame'
export type ClientInteractionDependency = 'attachments' | 'configuration' | 'session' | 'mutation_lane'
export type ClientInteractionFailure =
  | 'preparation_failed'
  | 'attachment_loss'
  | 'session_mismatch'
  | 'payload_mismatch'
  | 'transport_failed'
  | 'handler_rejected'
export type ClientInteractionUnknown =
  | 'hidden'
  | 'not_started'
  | 'visibility_unsupported'
  | 'observer_failed'
  | 'observation_capacity'
  | 'feedback_unobserved'
  | 'input_start_unverified'
  | 'handoff_unobserved'
  | 'payload_unverified'
  | 'session_unverified'
  | 'stop_target_unverified'
  | 'stop_target_changed'
  | 'cancelled'
  | 'superseded'
  | 'reset'
  | 'disposed'
  | 'page_exit'

export interface ClientInteractionBinding {
  readonly session_id?: string
  readonly environment_id?: string
  readonly task_id?: string
  readonly chat_id?: string
}

export interface ClientInteractionStart extends ClientInteractionReceipt {
  readonly client_interaction_id: string
  readonly client_view_id: string
  readonly interaction_kind: ClientInteractionKind
  readonly surface: UiReadinessSurface
  readonly role: UiReadinessRole
  readonly command_id?: string
  readonly command_revision?: number
  readonly handoff_scope?: 'single_action' | 'send_now_and_remove_queued' | 'not_applicable'
  readonly eventTimeStamp?: number
}

export interface ClientInteractionReceipt extends ClientInteractionBinding {
  readonly submission_request_id?: string
  readonly turn_id?: string
  readonly pending_message_id?: string
}

export type ClientInteractionObservation =
  | {readonly observation: 'committed'}
  | {readonly observation: 'preparation_started'}
  | {readonly observation: 'preparation_completed'; readonly preparation_elapsed_ms: number}
  | {readonly observation: 'dependency_wait_started'; readonly dependency: ClientInteractionDependency}
  | {
      readonly observation: 'dependency_wait_ended'
      readonly dependency: ClientInteractionDependency
      readonly dependency_wait_elapsed_ms: number
    }
  | {
      readonly observation: 'feedback_observed'
      readonly feedback_method: ClientInteractionFeedbackMethod
      readonly feedback_coverage: 'semantic_visibility_not_paint'
      readonly feedback_elapsed_ms: number
    }
  | {
      readonly observation: 'transport_handoff'
      readonly handoff_method: 'transport_send'
      readonly payload_integrity: 'verified'
      readonly session_integrity: 'verified'
      readonly handoff_elapsed_ms: number
    }
  | {readonly observation: 'failed'; readonly reason: ClientInteractionFailure}
  | {readonly observation: 'unresolved'; readonly reason: ClientInteractionUnknown}
  | {
      readonly observation: 'completed'
      readonly feedback_event_id: string
      readonly handoff_event_id?: string
    }

export type ClientInteractionFact = typeof CLIENT_INTERACTION_POLICY &
  Omit<ClientInteractionStart, 'eventTimeStamp'> &
  ClientInteractionReceipt & {
    readonly event_id: string
    readonly committed_event_id: string
    readonly clock_id: string
    readonly sequence: number
    readonly observed_at_ms: number
    readonly monotonic_ms: number
    readonly input_start_ms: number
    readonly input_time_source: 'event_timestamp' | 'handler_fallback'
    readonly measurement_coverage: 'full_input_clock' | 'input_start_unverified'
    readonly input_queue_ms?: number
    readonly visibility: UiReadinessVisibility
    readonly visibility_interrupted?: 'true'
    readonly required_obligations: 'feedback' | 'feedback|handoff'
  } & ClientInteractionObservation

export interface ClientInteraction {
  readonly id: string
  readonly kind: ClientInteractionKind
  readonly commandId?: string
  readonly commandRevision?: number
  readonly started: BrowserObservationTime
  preparationStarted(): void
  preparationCompleted(): void
  dependencyChanged(dependency: ClientInteractionDependency, waiting: boolean): void
  feedback(method: ClientInteractionFeedbackMethod): void
  handoff(
    receipt: ClientInteractionReceipt,
    payloadMatches: boolean | undefined,
    sessionMatches: boolean | undefined,
  ): void
  failed(reason: ClientInteractionFailure): void
  unresolved(reason: ClientInteractionUnknown): void
}
